import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Dot,
} from "recharts";
import { Target, HelpCircle, TrendingUp, TrendingDown, Zap } from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { formatLevel } from "@/lib/skillLevels";
import type { LevelHistory } from "@shared/schema";

interface LevelEvolutionChartProps {
  currentLevel: number;
  onInfoClick?: () => void;
  isFullscreen?: boolean;
  userId?: string; // Optional userId to fetch history for other players
}

// Custom dot component for chart points
const CustomDot = (props: any) => {
  const { cx, cy, index, dataLength, value } = props;
  const isLast = index === dataLength - 1;
  const numValue = Number(value) || 0;
  
  if (isLast) {
    // Highlighted current level indicator
    return (
      <g>
        {/* Outer glow */}
        <circle cx={cx} cy={cy} r={16} fill="hsl(var(--primary))" opacity={0.2} />
        <circle cx={cx} cy={cy} r={12} fill="hsl(var(--primary))" opacity={0.3} />
        {/* Inner circle */}
        <circle cx={cx} cy={cy} r={8} fill="hsl(var(--primary))" stroke="white" strokeWidth={2} />
        
        {/* Value label directly over the dot - ensure it shows the correct current level */}
        <g transform={`translate(${cx}, ${cy - 25})`}>
          <rect 
            x="-20" 
            y="-10" 
            width="40" 
            height="20" 
            rx="10" 
            fill="hsl(var(--primary))" 
            className="shadow-lg"
          />
          <text 
            x="0" 
            y="4" 
            textAnchor="middle" 
            fill="white" 
            fontSize="12" 
            fontWeight="bold"
          >
            {numValue.toFixed(2)}
          </text>
          {/* Small triangle pointer */}
          <path 
            d="M -4 10 L 0 14 L 4 10 Z" 
            fill="hsl(var(--primary))" 
          />
        </g>
      </g>
    );
  }
  
  // Regular dots
  return (
    <circle 
      cx={cx} 
      cy={cy} 
      r={4} 
      fill="hsl(var(--primary))" 
      stroke="white" 
      strokeWidth={1.5} 
    />
  );
};

export function LevelEvolutionChart({ currentLevel, onInfoClick, isFullscreen = false, userId }: LevelEvolutionChartProps) {
  const { data: levelHistory, isLoading } = useQuery<LevelHistory[]>({
    queryKey: userId ? [`/api/profile/level-history`, { userId }] : ["/api/profile/level-history"],
    queryFn: async () => {
      const url = userId 
        ? `/api/profile/level-history?userId=${userId}`
        : "/api/profile/level-history";
      const res = await fetch(url);
      if (!res.ok) throw new Error("Failed to fetch level history");
      return res.json();
    },
    staleTime: 30000, // Avoid excessive refetching
  });

  const chartData = levelHistory
    ? [...levelHistory]
        .reverse()
        .map((entry: any, index, arr) => {
          const entryDate = new Date(entry.matchPlayedAt || entry.createdAt!);
          return {
            id: entry.id,
            xAxisKey: `${format(entryDate, "d MMM", { locale: es })} - ${index}`,
            date: format(entryDate, "d MMM", { locale: es }),
            level: entry.newLevel,
            change: entry.change,
            reason: entry.reason,
            fullDate: format(entryDate, "d 'de' MMMM, yyyy 'a las' HH:mm", { locale: es }),
            isLast: index === arr.length - 1,
          };
        })
    : [];

  const hasHistory = chartData.length > 0;
  
  // Always use the profile's currentLevel as the authoritative value
  // Level history entries may be stale if matches were approved out of chronological order
  const effectiveCurrentLevel = currentLevel;

  // Calculate statistics - only count actual match entries (not initial onboarding)
  const matchEntries = levelHistory?.filter(h => h.reason === 'match_win' || h.reason === 'match_loss') || [];
  const stats = levelHistory && levelHistory.length > 0 ? {
    maxLevel: Math.max(...levelHistory.map(h => h.newLevel)),
    minLevel: Math.min(...levelHistory.map(h => h.newLevel)),
    avgChange: matchEntries.length > 0 ? (matchEntries.reduce((sum, h) => sum + h.change, 0) / matchEntries.length).toFixed(3) : "0",
    totalMatches: matchEntries.length,
    wins: matchEntries.filter(h => h.reason === 'match_win').length,
    losses: matchEntries.filter(h => h.reason === 'match_loss').length,
  } : null;

  const lastChange = levelHistory && levelHistory.length > 0 
    ? levelHistory[0].change
    : 0;
  const isPositive = lastChange > 0;

  // Calculate domain for Y axis - strictly match min/max values with extra padding (0.05)
  const minLevelValue = hasHistory ? Math.min(...chartData.map(d => d.level)) : effectiveCurrentLevel;
  const maxLevelValue = hasHistory ? Math.max(...chartData.map(d => d.level)) : effectiveCurrentLevel;
  
  // Apply requested margins
  const minDomain = minLevelValue - 0.05;
  const maxDomain = maxLevelValue + 0.05;
  const yDomain = [minDomain, maxDomain];

  // Modern chart component used in both views
  const ModernChart = ({ height = "h-64" }: { height?: string }) => (
    <div className={`${height} min-h-[250px] relative`}>
      {/* Min/Max labels on the right side - showing the padded values */}
      {hasHistory && stats && (
        <div className="absolute right-0 top-0 bottom-0 flex flex-col justify-between py-1 z-10 pointer-events-none">
          <span className="text-xs text-muted-foreground font-medium bg-background/80 px-1 rounded">
            {maxDomain.toFixed(2)}
          </span>
          <span className="text-xs text-muted-foreground font-medium bg-background/80 px-1 rounded">
            {minDomain.toFixed(2)}
          </span>
        </div>
      )}
      
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart 
          data={chartData} 
          margin={{ top: 25, right: 40, left: 10, bottom: 5 }}
        >
          <defs>
            <linearGradient id="levelGradientModern" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.4} />
              <stop offset="50%" stopColor="hsl(var(--primary))" stopOpacity={0.2} />
              <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.05} />
            </linearGradient>
          </defs>
          
          {/* Use xAxisKey for data positioning to prevent grouping of same-day entries */}
          <XAxis 
            dataKey="xAxisKey" 
            hide={true}
          />
          <YAxis 
            domain={yDomain}
            hide={true}
          />
          
          {/* Horizontal reference line at current level */}
          <ReferenceLine
            y={effectiveCurrentLevel}
            stroke="hsl(var(--muted-foreground))"
            strokeDasharray="4 4"
            strokeOpacity={0.3}
          />
          
          <Tooltip
            content={({ active, payload }) => {
              if (active && payload && payload.length) {
                const data = payload[0].payload;
                // Find all matches for the same day to show count
                const sameDayMatches = chartData.filter(d => d.date === data.date);
                const matchCount = sameDayMatches.length;
                const matchIndex = sameDayMatches.findIndex(m => m.xAxisKey === data.xAxisKey) + 1;

                const calculatedChange = data.change || 0;

                return (
                  <div className="bg-popover border border-border rounded-lg p-3 shadow-xl">
                    <p className="text-xs text-muted-foreground mb-1">
                      {data.fullDate} 
                      {matchCount > 1 && ` (Partido ${matchIndex} de ${matchCount})`}
                    </p>
                    <p className="text-lg font-bold text-primary">Nivel {data.level.toFixed(2)}</p>
                    {calculatedChange !== 0 && (
                      <p
                        className={`text-sm font-medium mt-1 ${
                          calculatedChange > 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"
                        }`}
                      >
                        {calculatedChange > 0 ? "+" : ""}
                        {calculatedChange.toFixed(2)} ({data.reason === "match_win" ? "Victoria" : "Derrota"})
                      </p>
                    )}
                    {matchCount > 1 && (
                      <div className="mt-2 pt-2 border-t border-border">
                        <p className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider">
                          Partidos del día: {matchCount}
                        </p>
                      </div>
                    )}
                  </div>
                );
              }
              return null;
            }}
          />
          
          <Area
            type="monotone"
            dataKey="level"
            stroke="hsl(var(--primary))"
            strokeWidth={3}
            fill="url(#levelGradientModern)"
            dot={(props: any) => (
              <CustomDot {...props} dataLength={chartData.length} value={props.payload.level} />
            )}
            activeDot={{
              r: 6,
              fill: "hsl(var(--primary))",
              stroke: "white",
              strokeWidth: 2,
            }}
          />
        </AreaChart>
      </ResponsiveContainer>
      
      {/* Current level badge overlay removed since it's now in the Dot component */}
    </div>
  );

  // Simple version for profile
  if (!isFullscreen) {
    return (
      <div data-testid="card-level-evolution" className="w-full h-[300px] min-h-[300px] overflow-hidden">
        {isLoading ? (
          <div className="h-full flex items-center justify-center">
            <div className="animate-pulse text-muted-foreground text-sm">
              Cargando historial...
            </div>
          </div>
        ) : hasHistory ? (
          <ModernChart height="h-full" />
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-center p-4">
            <Target className="h-12 w-12 text-muted-foreground/20 mb-4" />
            <p className="text-sm text-muted-foreground">
              Historial aparecerá aquí con tus partidos
            </p>
          </div>
        )}
      </div>
    );
  }

  // Full version for modal/drawer
  return (
    <div className="w-full h-full">
      <Card data-testid="card-level-evolution" className="h-full flex flex-col">
        <CardContent className="flex-1 overflow-y-auto flex flex-col gap-4 pb-12 px-3 sm:px-6">
          {/* Current Level and Change - Always shown */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-sm text-muted-foreground mb-2">Nivel Actual</p>
                  <p className="text-4xl font-bold text-primary" data-testid="text-current-level">
                    {formatLevel(effectiveCurrentLevel)}
                  </p>
                  {hasHistory && lastChange !== 0 && (
                    <span
                      className={`inline-flex items-center text-sm font-medium mt-2 ${
                        isPositive ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"
                      }`}
                      data-testid="text-level-change"
                    >
                      {isPositive ? (
                        <TrendingUp className="h-4 w-4 mr-1" />
                      ) : (
                        <TrendingDown className="h-4 w-4 mr-1" />
                      )}
                      {isPositive ? "+" : ""}
                      {lastChange.toFixed(2)} (último cambio)
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Stats Grid - Only shown in fullscreen */}
            {stats && (
              <>
                <Card className="bg-gradient-to-br from-victory/10 to-victory/5 border-victory/20">
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground mb-2">Máximo Nivel</p>
                      <p className="text-3xl font-bold text-victory">
                        {stats.maxLevel.toFixed(2)}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">en {stats.totalMatches} partidos</p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-defeat/10 to-defeat/5 border-defeat/20">
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground mb-2">Mínimo Nivel</p>
                      <p className="text-3xl font-bold text-defeat">
                        {stats.minLevel.toFixed(2)}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">Cambio promedio: {stats.avgChange}</p>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>

          {/* Chart */}
          {isLoading ? (
            <div className="h-48 sm:h-64 flex items-center justify-center">
              <div className="animate-pulse text-muted-foreground text-sm">
                Cargando historial...
              </div>
            </div>
          ) : hasHistory ? (
            <Card className="overflow-hidden">
              <CardContent className="pt-4 sm:pt-6 px-2 sm:px-4">
                <ModernChart height="h-56 sm:h-72 lg:h-80" />
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="pt-8 pb-8 sm:pt-12 sm:pb-12">
                <div className="h-40 sm:h-64 flex flex-col items-center justify-center text-center">
                  <Target className="h-16 w-16 text-muted-foreground/20 mb-4" />
                  <p className="text-lg font-medium text-muted-foreground mb-2">
                    Tu historial aparecerá aquí
                  </p>
                  <p className="text-sm text-muted-foreground max-w-xs">
                    Registra partidos para comenzar a rastrear tu evolución de nivel
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Match Stats - Only shown in fullscreen */}
          {stats && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Zap className="h-5 w-5 text-primary" />
                  Resumen de Partidos
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
                  <div className="text-center px-1 sm:px-0">
                    <p className="text-xl sm:text-2xl font-bold text-victory">{stats.wins}</p>
                    <p className="text-xs sm:text-sm text-muted-foreground">Victorias</p>
                  </div>
                  <div className="text-center px-1 sm:px-0">
                    <p className="text-xl sm:text-2xl font-bold text-defeat">{stats.losses}</p>
                    <p className="text-xs sm:text-sm text-muted-foreground">Derrotas</p>
                  </div>
                  <div className="text-center px-1 sm:px-0">
                    <p className="text-xl sm:text-2xl font-bold text-primary">
                      {stats.totalMatches > 0 ? Math.round((stats.wins / stats.totalMatches) * 100) : 0}%
                    </p>
                    <p className="text-xs sm:text-sm text-muted-foreground">Winrate</p>
                  </div>
                  <div className="text-center px-1 sm:px-0">
                    <p className="text-xl sm:text-2xl font-bold">{stats.totalMatches}</p>
                    <p className="text-xs sm:text-sm text-muted-foreground">Total</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <p className="text-center text-xs text-muted-foreground">
            Se ajusta automáticamente con cada partido aprobado
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
